/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { Column } from "../../../../common/commonComponents/lotDetailsTable";
import {
	PhyInspCondition,
	PhyInspContainer,
} from "../platePhysicalInspection.types";

export const getColumnsForPhyInspContainers = (
	t: any,
	inspConditions: PhyInspCondition[],
	handleConditionChange: (row: PhyInspContainer, condition: string) => void,
	handleReasonChange: (row: PhyInspContainer, reason: string) => void,
	errors: any,
): Column[] => [
	{
		header: t("postAutoclaveProcess.physicalInspection.columns.plateId"),
		key: "container_id",
		render: (row: PhyInspContainer) => (
			<>
				<div className="flex items-center gap-3 -mt-3">
					<div
						className={`w-2 h-2 rounded-full ${row.rejected === false ? "bg-emerald-500" : row.rejected === true ? "bg-red-500" : "bg-slate-300"}`}
					></div>
					<span className="text-sm font-black text-slate-700 tracking-tight">
						{row.container_serial_number}
					</span>
				</div>
			</>
		),
	},
	{
		header: t(
			"postAutoclaveProcess.physicalInspection.columns.physicalCondition",
		),
		key: "container_id",
		render: (row: PhyInspContainer) => (
			<>
				<div className="relative max-w-md">
					<select
						value={
							inspConditions.find(
								(cond) => Number(cond.id) === row.inspection_reason_id,
							)?.id ?? ""
						}
						onChange={(e) => handleConditionChange(row, e.target.value)}
						className="w-full px-3 py-2.5 bg-white border rounded-xl text-xs font-medium outline-none transition-all whitespace-nowrap"
					>
						<option value="" disabled>
							{t(
								"postAutoclaveProcess.physicalInspection.selectCondition",
							)}
						</option>
						{inspConditions.map((cond) => (
							<option key={cond.id} value={cond.id}>
								{cond.condition_name}
							</option>
						))}
					</select>
					<div className="h-3 mt-1">
					{errors.containers[row.container_id] &&
						!row.rejected && (
							<p className="text-[11px] text-red-500">
								{errors.containers[row.container_id]}
							</p>
					)}
					</div>
				</div>
			</>
		),
	},
	{
		header: t("postAutoclaveProcess.physicalInspection.columns.result"),
		key: "container_id",
		render: (row: PhyInspContainer) => (
			<>
				<div className="flex items-center gap-2 whitespace-nowrap -mt-4">
					{row.rejected === false && (
						<span className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-xl text-[10px] font-black uppercase tracking-widest border border-emerald-200 flex items-center gap-1">
							<span className="material-symbols-outlined text-sm">
								check_circle
							</span>
							{t("postAutoclaveProcess.physicalInspection.pass")}
						</span>
					)}
					{row.rejected === true && (
						<span className="px-4 py-2 bg-red-100 text-red-700 rounded-xl text-[10px] font-black uppercase tracking-widest border border-red-200 flex items-center gap-1">
							<span className="material-symbols-outlined text-sm">
								cancel
							</span>
							{t("postAutoclaveProcess.physicalInspection.rejectThisOne")}
						</span>
					)}
					{row.rejected === undefined && (
						<span className="px-4 py-2 bg-slate-100 text-slate-400 rounded-xl text-[10px] font-black uppercase tracking-widest border border-slate-200 flex items-center gap-1">
							<span className="material-symbols-outlined text-sm">
								hourglass_empty
							</span>
							{t("postAutoclaveProcess.physicalInspection.pending")}
						</span>
					)}
				</div>
			</>
		),
	},
	{
		header: t(
			"postAutoclaveProcess.physicalInspection.columns.rejectionReason",
		),
		key: "container_id",
		className: "text-right",
		render: (row: PhyInspContainer) => (
			<>
				<div className="relative max-w-md">
					<input
						type="text"
						placeholder={
							row.rejected === true
								? "Enter reason for rejection..."
								: "N/A"
						}
						disabled={row.rejected !== true}
						value={row.rejection_reason ?? ""}
						onChange={(e) => handleReasonChange(row, e.target.value)}
						className={`w-full px-4 py-2.5 bg-white border rounded-xl text-xs font-medium outline-none transition-all ${
							row.rejected === true &&
							!(row.rejection_reason ?? "").trim()
								? "border-red-300 ring-2 ring-red-50 focus:border-red-500"
								: "border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-50"
						} disabled:bg-slate-50 disabled:text-slate-300 disabled:border-slate-100`}
					/>
					{row.rejected === true &&
						!(row.rejection_reason ?? "").trim() && (
							<div className="absolute -top-2 -right-2 bg-red-500 text-white w-4 h-4 rounded-full flex items-center justify-center shadow-sm animate-bounce">
								<span className="material-symbols-outlined text-[10px] font-bold">
									priority_high
								</span>
							</div>
					)}
					<div className="h-3 mt-1">
					{errors.containers[row.container_id] &&
						row.rejected && (
							<p className="text-[11px] text-red-500 mt-1">
								{errors.containers[row.container_id]}
							</p>
					)}
					</div>
				</div>
			</>
		),
	},
	{
		header: t("postAutoclaveProcess.physicalInspection.columns.finalStatus"),
		key: "container_id",
		render: (row: PhyInspContainer) => (
			<>
				<span
					className={`text-xs font-black tracking-tight whitespace-nowrap -mt-3 ${
						row.rejected === false
							? "text-emerald-600"
							: row.rejected === true
								? "text-red-600"
								: "text-slate-400 italic"
					}`}
				>
					{row.rejected === false
						? t("postAutoclaveProcess.physicalInspection.passed")
						: row.rejected === true
							? t("postAutoclaveProcess.physicalInspection.rejected")
							: t(
									"postAutoclaveProcess.physicalInspection.awaitingInspection",
								)}
				</span>
			</>
		),
	},
];
